import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Loader2, Trash2, Eye, Plus, Inbox, FileText, Mail, ClipboardList, CalendarCheck, MessageSquare, Award, Tag, X } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { IMAGES } from "@/lib/content";

const TABS = [
  { id: "overview", label: "Overview", icon: ClipboardList },
  { id: "submissions", label: "Submissions", icon: Inbox },
  { id: "cases", label: "Case Studies", icon: Award },
  { id: "articles", label: "Articles", icon: FileText },
  { id: "newsletter", label: "Newsletter", icon: Mail },
  { id: "pricing", label: "Pricing", icon: Tag },
];

const TYPE_META = {
  audit: { label: "Audit", icon: ClipboardList },
  consultation: { label: "Consultation", icon: CalendarCheck },
  contact: { label: "Contact", icon: MessageSquare },
};

const CASE_CATEGORIES = ["Hospitality", "Luxury Villas", "Fine Dining", "Marine & Services", "Professional Services"];
const IMAGE_OPTIONS = Object.entries(IMAGES).filter(([k]) => k !== "logo");

const EMPTY_ARTICLE = { title: "", slug: "", excerpt: "", content: "", cover_image: IMAGES.harbor, read_time: "5 min read", published: true };
const EMPTY_CASE = { name: "", slug: "", category: "Hospitality", title: "", metric: "", metric_label: "", quote: "", author: "", image: IMAGES.resort, order: 99 };

function FoundingControl() {
  const [signed, setSigned] = useState(0);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    API.get("/founding-offer").then(({ data }) => setSigned(data.signed)).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await API.put("/admin/settings/founding", { signed: Number(signed) });
      toast.success("Founding offer updated on the site.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="gold-ring-card mt-6 flex flex-wrap items-center justify-between gap-4 p-6" data-testid="founding-control">
      <div>
        <p className="font-serif text-lg text-ivory">Founding client offer</p>
        <p className="mt-1 text-xs text-slate-500">Shown live on the Home and Case Studies pages</p>
      </div>
      <div className="flex items-center gap-3">
        <select value={signed} onChange={(e) => setSigned(e.target.value)} className="input-lux w-36" data-testid="founding-select">
          {[0, 1, 2, 3].map((n) => (
            <option key={n} value={n} className="bg-navy-deep">{n} of 3 signed</option>
          ))}
        </select>
        <button onClick={save} disabled={saving} data-testid="founding-save-btn" className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-semibold text-navy-deep hover:bg-gold-light disabled:opacity-50">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Update"}
        </button>
      </div>
    </div>
  );
}

function CalendarConnect() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    API.get("/admin/calendar/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);
  const connect = async () => {
    try {
      const { data } = await API.get("/admin/calendar/connect");
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err, "Google Calendar is not configured yet."));
    }
  };
  if (!status) return null;
  return (
    <div className="gold-ring-card mt-6 flex flex-wrap items-center justify-between gap-4 p-6" data-testid="calendar-connect-card">
      <div>
        <p className="font-serif text-lg text-ivory">Google Calendar</p>
        <p className="mt-1 text-xs text-slate-500">
          {status.connected
            ? "Connected — audit call requests create events on your calendar automatically"
            : status.configured
              ? "Credentials saved — connect your calendar to auto-create audit call events"
              : "Audit bookings already arrive with a calendar file attached. Add Google credentials to auto-create events."}
        </p>
      </div>
      {status.connected ? (
        <span className="rounded-full bg-emerald-500/15 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-emerald-400" data-testid="calendar-connected-badge">Connected</span>
      ) : (
        <button onClick={connect} disabled={!status.configured} data-testid="calendar-connect-btn" className="rounded-full bg-gold px-5 py-2.5 text-xs font-semibold text-navy-deep hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-40">
          Connect Google Calendar
        </button>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState("");
  const [articles, setArticles] = useState([]);
  const [cases, setCases] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editingCase, setEditingCase] = useState(null);
  const navigate = useNavigate();

  const loadStats = useCallback(() => API.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}), []);
  const loadSubs = useCallback((t = filter) => API.get(`/admin/submissions${t ? `?type=${t}` : ""}`).then(({ data }) => setSubs(data)).catch(() => {}), [filter]);
  const loadArticles = useCallback(() => API.get("/admin/articles").then(({ data }) => setArticles(data)).catch(() => {}), []);
  const loadCases = useCallback(() => API.get("/case-studies").then(({ data }) => setCases(data)).catch(() => {}), []);
  const loadSubscribers = useCallback(() => API.get("/admin/newsletter").then(({ data }) => setSubscribers(data)).catch(() => {}), []);
  const [prices, setPrices] = useState([]);
  const [savingPrices, setSavingPrices] = useState(false);
  const loadPrices = useCallback(() => API.get("/admin/settings/pricing").then(({ data }) => setPrices(data)).catch(() => {}), []);

  useEffect(() => {
    API.get("/auth/me")
      .then(({ data }) => { setUser(data); loadStats(); })
      .catch(() => navigate("/admin/login"))
      .finally(() => setChecking(false));
  }, [navigate, loadStats]);

  useEffect(() => {
    if (!user) return;
    if (tab === "submissions") loadSubs();
    if (tab === "articles") loadArticles();
    if (tab === "cases") loadCases();
    if (tab === "newsletter") loadSubscribers();
    if (tab === "pricing") loadPrices();
  }, [tab, user, loadSubs, loadArticles, loadCases, loadSubscribers, loadPrices]);

  const logout = async () => {
    await API.post("/auth/logout").catch(() => {});
    localStorage.removeItem("eid_token");
    navigate("/admin/login");
  };

  const markRead = async (id) => {
    await API.patch(`/admin/submissions/${id}`, { status: "read" }).catch(() => {});
    loadSubs();
    loadStats();
  };

  const removeSub = async (id) => {
    await API.delete(`/admin/submissions/${id}`).catch(() => {});
    toast.success("Submission deleted");
    loadSubs();
    loadStats();
  };

  const saveArticle = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) await API.patch(`/admin/articles/${editing.id}`, editing);
      else await API.post("/admin/articles", editing);
      toast.success(editing.id ? "Article updated" : "Article published");
      setEditing(null);
      loadArticles();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeArticle = async (id) => {
    await API.delete(`/admin/articles/${id}`).catch(() => {});
    toast.success("Article deleted");
    loadArticles();
    loadStats();
  };

  const saveCase = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editingCase, order: Number(editingCase.order) || 99 };
      if (payload.id) await API.patch(`/admin/case-studies/${payload.id}`, payload);
      else await API.post("/admin/case-studies", payload);
      toast.success(payload.id ? "Case study updated" : "Case study added");
      setEditingCase(null);
      loadCases();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeCase = async (id) => {
    await API.delete(`/admin/case-studies/${id}`).catch(() => {});
    toast.success("Case study deleted");
    loadCases();
    loadStats();
  };

  const savePrices = async () => {
    setSavingPrices(true);
    try {
      await API.put("/admin/settings/pricing", prices);
      toast.success("Pricing saved — new reports will quote these rates.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPrices(false);
    }
  };

  if (checking) {
    return (
      <main className="flex min-h-screen items-center justify-center" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </main>
    );
  }
  if (!user) return null;

  return (
    <main className="mx-auto max-w-7xl px-6 pt-32 pb-24 lg:px-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow">Admin Console</p>
          <h1 className="mt-2 font-serif text-3xl text-ivory">Welcome back</h1>
          <p className="mt-1 font-mono text-xs text-slate-500">{user.email}</p>
        </div>
        <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 rounded-full border border-gold/30 px-5 py-2.5 text-xs font-medium text-gold-glow transition-colors duration-300 hover:bg-gold/10">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>

      <div className="mt-8 flex flex-wrap gap-2 border-b border-gold/10 pb-4" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-colors duration-300 ${tab === t.id ? "bg-gold text-navy-deep" : "border border-gold/25 text-slate-300 hover:border-gold/50"}`}
          >
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <div className="mt-10" data-testid="admin-overview">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Audit requests", stats.submissions?.audit?.total || 0, stats.submissions?.audit?.new || 0],
              ["Consultations", stats.submissions?.consultation?.total || 0, stats.submissions?.consultation?.new || 0],
              ["Contact messages", stats.submissions?.contact?.total || 0, stats.submissions?.contact?.new || 0],
              ["Newsletter subscribers", stats.newsletter_count, 0],
            ].map(([label, total, fresh]) => (
              <div key={label} className="gold-ring-card p-7">
                <p className="text-xs uppercase tracking-widest text-slate-500">{label}</p>
                <p className="mt-3 font-serif text-4xl text-ivory">{total}</p>
                {fresh > 0 && <p className="mt-2 inline-block rounded-full bg-gold/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-gold-glow">{fresh} new</p>}
              </div>
            ))}
          </div>
          <FoundingControl />
          <CalendarConnect />
        </div>
      )}

      {tab === "submissions" && (
        <div className="mt-8" data-testid="admin-submissions">
          <div className="flex flex-wrap gap-2">
            {["", "audit", "consultation", "contact"].map((t) => (
              <button
                key={t || "all"}
                onClick={() => { setFilter(t); loadSubs(t); }}
                data-testid={`sub-filter-${t || "all"}`}
                className={`rounded-full border px-4 py-1.5 text-xs transition-colors duration-300 ${filter === t ? "border-gold bg-gold/15 text-gold-glow" : "border-slate-600 text-slate-400"}`}
              >
                {t ? TYPE_META[t].label : "All"}
              </button>
            ))}
          </div>
          <div className="mt-6 space-y-4">
            {subs.length === 0 && <p className="py-10 text-center text-sm text-slate-500" data-testid="subs-empty">No submissions yet.</p>}
            {subs.map((s) => {
              const Meta = TYPE_META[s.type] || TYPE_META.contact;
              return (
                <div key={s.id} className={`gold-ring-card p-6 ${s.status === "new" ? "border-gold/40" : ""}`} data-testid={`sub-row-${s.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        <Meta.icon className="h-4 w-4 text-gold" />
                        <span className="font-mono text-[10px] uppercase tracking-widest text-gold-glow">{Meta.label}</span>
                        {s.status === "new" && <span className="rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-semibold text-gold-light">NEW</span>}
                        <span className="text-[11px] text-slate-600">{new Date(s.created_at).toLocaleString()}</span>
                      </div>
                      <p className="mt-3 font-serif text-lg text-ivory">{s.name || "—"} · {s.business || "—"}</p>
                      <p className="mt-1 text-xs text-slate-400">{s.email}{s.phone ? ` · ${s.phone}` : ""}{s.website ? ` · ${s.website}` : ""}</p>
                      {Object.keys(s.meta || {}).length > 0 && (
                        <p className="mt-2 text-xs text-slate-500">{Object.entries(s.meta).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>
                      )}
                      {s.message && <p className="mt-3 max-w-2xl text-sm font-light text-slate-300">“{s.message}”</p>}
                    </div>
                    <div className="flex gap-2">
                      {s.type === "audit" && (
                        <button
                          onClick={async () => {
                            const { data } = await API.post(`/admin/submissions/${s.id}/report`);
                            navigate(`/admin/reports/${data.id}`);
                          }}
                          data-testid={`sub-report-${s.id}`}
                          className="rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-widest text-gold-glow hover:bg-gold/20"
                        >
                          Draft report
                        </button>
                      )}
                      {s.status === "new" && (
                        <button onClick={() => markRead(s.id)} data-testid={`sub-read-${s.id}`} className="rounded-full border border-gold/30 p-2.5 text-gold-glow hover:bg-gold/10" aria-label="Mark read">
                          <Eye className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={() => removeSub(s.id)} data-testid={`sub-delete-${s.id}`} className="rounded-full border border-red-500/30 p-2.5 text-red-400 hover:bg-red-500/10" aria-label="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "cases" && (
        <div className="mt-8" data-testid="admin-cases">
          <button onClick={() => setEditingCase({ ...EMPTY_CASE })} data-testid="case-new-btn" className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-semibold text-navy-deep hover:bg-gold-light">
            <Plus className="h-4 w-4" /> New case study
          </button>
          <div className="mt-6 space-y-4">
            {cases.map((c) => (
              <div key={c.id} className="gold-ring-card flex flex-wrap items-center justify-between gap-4 p-6" data-testid={`case-row-${c.slug}`}>
                <div className="flex items-center gap-5">
                  <img src={c.image} alt="" className="h-14 w-20 rounded-lg object-cover duotone" />
                  <div>
                    <p className="font-serif text-lg text-ivory">{c.name} <span className="ml-2 font-mono text-xs text-gold-glow">{c.metric}</span></p>
                    <p className="mt-1 text-xs text-slate-500">{c.category} · order {c.order}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditingCase({ ...c })} data-testid={`case-edit-${c.slug}`} className="rounded-full border border-gold/30 px-4 py-2 text-xs text-gold-glow hover:bg-gold/10">Edit</button>
                  <button onClick={() => removeCase(c.id)} data-testid={`case-delete-${c.slug}`} className="rounded-full border border-red-500/30 p-2.5 text-red-400 hover:bg-red-500/10" aria-label="Delete case study">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "articles" && (
        <div className="mt-8" data-testid="admin-articles">
          <button onClick={() => setEditing({ ...EMPTY_ARTICLE })} data-testid="article-new-btn" className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-semibold text-navy-deep hover:bg-gold-light">
            <Plus className="h-4 w-4" /> New article
          </button>
          <div className="mt-6 space-y-4">
            {articles.map((a) => (
              <div key={a.id} className="gold-ring-card flex flex-wrap items-center justify-between gap-4 p-6" data-testid={`article-row-${a.slug}`}>
                <div className="flex items-center gap-5">
                  <img src={a.cover_image} alt="" className="h-14 w-20 rounded-lg object-cover duotone" />
                  <div>
                    <p className="font-serif text-lg text-ivory">{a.title}</p>
                    <p className="mt-1 text-xs text-slate-500">/{a.slug} · {a.published ? "Published" : "Draft"}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing({ ...a })} data-testid={`article-edit-${a.slug}`} className="rounded-full border border-gold/30 px-4 py-2 text-xs text-gold-glow hover:bg-gold/10">Edit</button>
                  <button onClick={() => removeArticle(a.id)} data-testid={`article-delete-${a.slug}`} className="rounded-full border border-red-500/30 p-2.5 text-red-400 hover:bg-red-500/10" aria-label="Delete article">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "pricing" && (
        <div className="mt-8" data-testid="admin-pricing">
          <p className="max-w-2xl text-sm text-slate-400">
            These rates are quoted word-for-word in the "Recommended next steps and prices" section of every audit
            report you generate. Edit them anytime — already-created reports keep the prices they were drafted with
            unless you redraft that section.
          </p>
          <div className="mt-6 space-y-4">
            {prices.map((p, i) => (
              <div key={p.package} className="gold-ring-card grid items-center gap-4 p-5 sm:grid-cols-12" data-testid={`price-row-${i}`}>
                <p className="font-serif text-lg text-ivory sm:col-span-3">{p.package}</p>
                <input
                  value={p.price}
                  onChange={(e) => setPrices(prices.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))}
                  placeholder="e.g. From US$2,500"
                  className="input-lux sm:col-span-4"
                  data-testid={`price-input-${i}`}
                />
                <input
                  value={p.note}
                  onChange={(e) => setPrices(prices.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))}
                  placeholder="Short note (optional)"
                  className="input-lux sm:col-span-5"
                  data-testid={`price-note-${i}`}
                />
              </div>
            ))}
          </div>
          <button
            onClick={savePrices}
            disabled={savingPrices || prices.length === 0}
            data-testid="pricing-save-btn"
            className="mt-6 flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-xs font-semibold text-navy-deep transition-colors duration-300 hover:bg-gold-light disabled:opacity-50"
          >
            {savingPrices ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save pricing"}
          </button>
        </div>
      )}

      {tab === "newsletter" && (
        <div className="mt-8" data-testid="admin-newsletter">
          <p className="text-sm text-slate-400">{subscribers.length} subscriber{subscribers.length === 1 ? "" : "s"} to The Caribbean Digital Dispatch.</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {subscribers.map((s) => (
              <div key={s.id} className="rounded-xl border border-gold/15 bg-navy-surface/60 px-5 py-4 text-sm text-slate-300" data-testid={`subscriber-${s.id}`}>
                {s.email}
                <p className="mt-1 text-[10px] text-slate-600">{new Date(s.created_at).toLocaleDateString()}</p>
              </div>
            ))}
            {subscribers.length === 0 && <p className="py-10 text-sm text-slate-500" data-testid="newsletter-empty">No subscribers yet.</p>}
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-navy-deep/90 p-6 backdrop-blur-md" data-testid="article-editor-modal">
          <form onSubmit={saveArticle} className="gold-ring-card w-full max-w-2xl space-y-4 p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl text-ivory">{editing.id ? "Edit article" : "New article"}</h2>
              <button type="button" onClick={() => setEditing(null)} data-testid="article-editor-close" className="rounded-full border border-gold/30 p-2 text-slate-300 hover:text-ivory"><X className="h-4 w-4" /></button>
            </div>
            <input required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Title" className="input-lux" data-testid="article-title-input" />
            <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="Slug (auto if empty)" className="input-lux" data-testid="article-slug-input" />
            <textarea value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} rows={2} placeholder="Excerpt" className="input-lux resize-none" data-testid="article-excerpt-input" />
            <textarea required value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} rows={8} placeholder="Article body — blank line between paragraphs" className="input-lux resize-none" data-testid="article-content-input" />
            <select value={editing.cover_image} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} className="input-lux" data-testid="article-cover-select">
              {IMAGE_OPTIONS.map(([k, v]) => (
                <option key={k} value={v} className="bg-navy-deep">{k}</option>
              ))}
            </select>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-slate-300">
                <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="article-published-toggle" /> Published
              </label>
              <button type="submit" data-testid="article-save-btn" className="rounded-full bg-gold px-6 py-2.5 text-xs font-semibold text-navy-deep hover:bg-gold-light">
                {editing.id ? "Save changes" : "Publish"}
              </button>
            </div>
          </form>
        </div>
      )}

      {editingCase && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-navy-deep/90 p-6 backdrop-blur-md" data-testid="case-editor-modal">
          <form onSubmit={saveCase} className="gold-ring-card w-full max-w-2xl space-y-4 p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl text-ivory">{editingCase.id ? "Edit case study" : "New case study"}</h2>
              <button type="button" onClick={() => setEditingCase(null)} data-testid="case-editor-close" className="rounded-full border border-gold/30 p-2 text-slate-300 hover:text-ivory"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input required value={editingCase.name} onChange={(e) => setEditingCase({ ...editingCase, name: e.target.value })} placeholder="Client name" className="input-lux" data-testid="case-name-input" />
              <select value={editingCase.category} onChange={(e) => setEditingCase({ ...editingCase, category: e.target.value })} className="input-lux" data-testid="case-category-select">
                {CASE_CATEGORIES.map((c) => <option key={c} value={c} className="bg-navy-deep">{c}</option>)}
              </select>
            </div>
            <input value={editingCase.title} onChange={(e) => setEditingCase({ ...editingCase, title: e.target.value })} placeholder="Headline — e.g. From OTA dependence to a direct booking engine" className="input-lux" data-testid="case-title-input" />
            <div className="grid gap-4 sm:grid-cols-3">
              <input value={editingCase.metric} onChange={(e) => setEditingCase({ ...editingCase, metric: e.target.value })} placeholder="Metric (e.g. +240%)" className="input-lux" data-testid="case-metric-input" />
              <input value={editingCase.metric_label} onChange={(e) => setEditingCase({ ...editingCase, metric_label: e.target.value })} placeholder="Metric label" className="input-lux" data-testid="case-metric-label-input" />
              <input type="number" value={editingCase.order} onChange={(e) => setEditingCase({ ...editingCase, order: e.target.value })} placeholder="Order" className="input-lux" data-testid="case-order-input" />
            </div>
            <textarea value={editingCase.quote} onChange={(e) => setEditingCase({ ...editingCase, quote: e.target.value })} rows={2} placeholder="Client quote" className="input-lux resize-none" data-testid="case-quote-input" />
            <input value={editingCase.author} onChange={(e) => setEditingCase({ ...editingCase, author: e.target.value })} placeholder="Quote attribution — e.g. General Manager, St. John's" className="input-lux" data-testid="case-author-input" />
            <select value={editingCase.image} onChange={(e) => setEditingCase({ ...editingCase, image: e.target.value })} className="input-lux" data-testid="case-image-select">
              {IMAGE_OPTIONS.map(([k, v]) => (
                <option key={k} value={v} className="bg-navy-deep">{k}</option>
              ))}
            </select>
            <div className="flex justify-end">
              <button type="submit" data-testid="case-save-btn" className="rounded-full bg-gold px-6 py-2.5 text-xs font-semibold text-navy-deep hover:bg-gold-light">
                {editingCase.id ? "Save changes" : "Add case study"}
              </button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}
